import type { ClientLogEntry } from "@/utils/logger";

const MAX_LOG_MESSAGE_LENGTH = 280;
const MAX_LOG_ENTRIES = 20;

type ReporterRole = "coach" | "leader" | "admin";

export interface BugReportFormInput {
  summary: string;
  details?: string;
}

export interface FeatureRequestFormInput {
  summary: string;
  details?: string;
}

interface ProductFeedbackContext {
  route: string;
  reporterRole: ReporterRole;
  reporterUserId: string;
  createdAt?: string;
}

interface BugReportContext extends ProductFeedbackContext {
  userAgent?: string;
  logs?: ClientLogEntry[];
}

export interface BugReportInsert {
  summary: string;
  details: string | null;
  route: string;
  reporter_role: ReporterRole;
  reporter_user_id: string;
  user_agent: string | null;
  client_log_bundle: ClientLogEntry[];
  screenshot_bucket: string | null;
  screenshot_path: string | null;
  screenshot_name: string | null;
  screenshot_mime_type: string | null;
  screenshot_size_bytes: number | null;
  status: "submitted";
  created_at: string;
}

export interface FeatureRequestInsert {
  summary: string;
  details: string | null;
  route: string;
  reporter_role: ReporterRole;
  reporter_user_id: string;
  decision_status: "new";
  created_at: string;
}

function normalizeText(value?: string) {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

export function sanitizeFeedbackLogEntries(entries?: ClientLogEntry[]) {
  return (entries ?? [])
    .map((entry) => {
      const message = entry.message.trim();
      if (!message) return null;

      return {
        ...entry,
        message:
          message.length > MAX_LOG_MESSAGE_LENGTH
            ? `${message.slice(0, MAX_LOG_MESSAGE_LENGTH)}...`
            : message,
      };
    })
    .filter((entry): entry is ClientLogEntry => entry !== null);
}

export function truncateFeedbackLogEntries(entries?: ClientLogEntry[], maxEntries = MAX_LOG_ENTRIES) {
  return sanitizeFeedbackLogEntries(entries).slice(-maxEntries);
}

export function buildBugReportPayload(input: BugReportFormInput, context: BugReportContext): BugReportInsert {
  return {
    summary: input.summary.trim(),
    details: normalizeText(input.details),
    route: context.route,
    reporter_role: context.reporterRole,
    reporter_user_id: context.reporterUserId,
    user_agent: normalizeText(context.userAgent),
    client_log_bundle: truncateFeedbackLogEntries(context.logs),
    screenshot_bucket: null,
    screenshot_path: null,
    screenshot_name: null,
    screenshot_mime_type: null,
    screenshot_size_bytes: null,
    status: "submitted",
    created_at: context.createdAt ?? new Date().toISOString(),
  };
}

export function buildFeatureRequestPayload(
  input: FeatureRequestFormInput,
  context: ProductFeedbackContext,
): FeatureRequestInsert {
  return {
    summary: input.summary.trim(),
    details: normalizeText(input.details),
    route: context.route,
    reporter_role: context.reporterRole,
    reporter_user_id: context.reporterUserId,
    decision_status: "new",
    created_at: context.createdAt ?? new Date().toISOString(),
  };
}
